import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import BuildIcon from "@mui/icons-material/Build";
import GrassIcon from "@mui/icons-material/Grass";
import api from "../../utils/api";
import { useClientConfig } from "../../utils/ClientConfigContext";

const OwnerDashboardSettings = () => {
  const { terminology, updateTerminology } = useClientConfig();

  const [settings, setSettings] = useState({
    show_violations: true,
    show_work_orders: true,
    show_architectural_requests: true,
    show_documents: true,
  });

  const [recordId, setRecordId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get("/owner-dashboard-settings");

      if (res.data) {
        setRecordId(res.data.id ?? null);
        setSettings({
          show_violations: !!res.data.show_violations,
          show_work_orders: !!res.data.show_work_orders,
          show_architectural_requests: !!res.data.show_architectural_requests,
          show_documents: !!res.data.show_documents,
        });
      }
    } catch (error) {
      setSaveError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (event) => {
    const { name, checked } = event.target;
    setSettings((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      if (recordId) {
        await api.put("/owner-dashboard-settings", settings);
      } else {
        const res = await api.post("/owner-dashboard-settings", settings);
        if (res.data?.settings?.id) {
          setRecordId(res.data.settings.id);
        }
      }

      setSaveSuccess(true);
    } catch (error) {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  const cardSx = {
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
    backgroundColor: "rgba(255,255,255,0.96)",
    p: { xs: 2, md: 2.5 },
  };

  const switchRowSx = {
    mt: 1.5,
    px: 1.25,
    py: 1.1,
    borderRadius: "12px",
    backgroundColor: "rgba(47,99,173,0.04)",
    border: "1px solid rgba(47,99,173,0.08)",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
        >
          Owner Dashboard Settings
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          Control which sections are visible on the owner dashboard.
        </Typography>
      </Box>

      <Paper elevation={0} sx={cardSx}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
        >
          Visible Dashboard Sections
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
          Enable or disable the sections shown to owners.
        </Typography>

        <Box sx={switchRowSx}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.show_violations}
                onChange={handleToggle}
                name="show_violations"
              />
            }
            label="Show Violations"
            sx={{ m: 0 }}
          />
        </Box>

        <Box sx={switchRowSx}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.show_work_orders}
                onChange={handleToggle}
                name="show_work_orders"
              />
            }
            label="Show Work Orders"
            sx={{ m: 0 }}
          />
        </Box>

        <Box sx={switchRowSx}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.show_architectural_requests}
                onChange={handleToggle}
                name="show_architectural_requests"
              />
            }
            label="Show Architectural Requests"
            sx={{ m: 0 }}
          />
        </Box>

        <Box sx={switchRowSx}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.show_documents}
                onChange={handleToggle}
                name="show_documents"
              />
            }
            label="Show Documents"
            sx={{ m: 0 }}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            startIcon={saving ? null : <SaveIcon />}
            disabled={saving}
            sx={primaryButtonSx}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : "Save Settings"}
          </Button>
        </Box>
      </Paper>

      {/* ── Terminology ──────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ ...cardSx, mt: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
          Work Order Terminology
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
          Choose how work orders are labelled throughout the owner-facing dashboard.
          This setting takes effect immediately. no page reload needed.
        </Typography>

        <ToggleButtonGroup
          value={terminology}
          exclusive
          onChange={(_e, val) => { if (val) updateTerminology(val); }}
          sx={{
            gap: 1.5,
            "& .MuiToggleButtonGroup-grouped": {
              border: "1px solid rgba(47,99,173,0.2) !important",
              borderRadius: "12px !important",
            },
          }}
        >
          <ToggleButton
            value="standard"
            sx={{
              px: 2.5,
              py: 1.25,
              textTransform: "none",
              fontWeight: 600,
              color: "#5f6b7a",
              flexDirection: "column",
              gap: 0.5,
              minWidth: 140,
              "&.Mui-selected": {
                backgroundColor: "rgba(47,99,173,0.08)",
                color: "#2f63ad",
                borderColor: "rgba(47,99,173,0.35) !important",
              },
            }}
          >
            <BuildIcon sx={{ fontSize: 22 }} />
            <Typography variant="body2" fontWeight={700}>Work Orders</Typography>
            <Typography variant="caption" sx={{ color: "inherit", opacity: 0.75 }}>Standard label</Typography>
          </ToggleButton>

          <ToggleButton
            value="heritage"
            sx={{
              px: 2.5,
              py: 1.25,
              textTransform: "none",
              fontWeight: 600,
              color: "#5f6b7a",
              flexDirection: "column",
              gap: 0.5,
              minWidth: 140,
              "&.Mui-selected": {
                backgroundColor: "rgba(46,125,50,0.08)",
                color: "#2e7d32",
                borderColor: "rgba(46,125,50,0.35) !important",
              },
            }}
          >
            <GrassIcon sx={{ fontSize: 22 }} />
            <Typography variant="body2" fontWeight={700}>Green Sheets</Typography>
            <Typography variant="caption" sx={{ color: "inherit", opacity: 0.75 }}>Heritage label</Typography>
          </ToggleButton>
        </ToggleButtonGroup>

        <Box
          sx={{
            mt: 2,
            px: 1.5,
            py: 1.25,
            borderRadius: "10px",
            backgroundColor: terminology === "heritage" ? "rgba(46,125,50,0.06)" : "rgba(47,99,173,0.04)",
            border: terminology === "heritage" ? "1px solid rgba(46,125,50,0.15)" : "1px solid rgba(47,99,173,0.08)",
          }}
        >
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            {terminology === "heritage"
              ? "Owners will see \"Green Sheets\" instead of \"Work Orders\" throughout their dashboard."
              : "Owners will see the standard \"Work Orders\" label throughout their dashboard."}
          </Typography>
        </Box>
      </Paper>

      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSaveSuccess(false)}>
          Dashboard settings saved successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={saveError}
        autoHideDuration={3000}
        onClose={() => setSaveError(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setSaveError(false)}>
          Failed to save dashboard settings.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OwnerDashboardSettings;